import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { Injectable } from "@angular/core";
import {
  FiscalProfile,
  FiscalProfileCreate,
  FiscalProfileLight,
  FiscalProfileUpdate
} from "../models/fiscal-profile/fiscal-profile";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { PaginatedResult } from "src/app/support/paginated-result";
import { map } from "rxjs/operators";
import { CacheIfFail } from "src/app/support/decorators/cache-if-fails-decorator";
import { SectionalCreate } from "src/app/backend/models/sectional/sectional.create";

@Injectable()
export class FiscalProfileBackend {
  constructor(private http: HttpClient) {}

  load(id: string): Observable<FiscalProfile> {
    return this.http.get<FiscalProfile>(
      `${environment.snoots.api.billing}/fiscal-profiles/${id}`
    );
  }

  loadLight(id: string): Observable<FiscalProfileLight> {
    return this.http.get<FiscalProfileLight>(
      `${environment.snoots.api.billing}/fiscal-profiles/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<FiscalProfile>> {
    return this.http.get<PaginatedResult<FiscalProfile>>(
      `${environment.snoots.api.billing}/fiscal-profiles`,
      {
        params: {
          p: request.page,
          s: request.pageSize,
          ...request.filterValues
        }
      }
    );
  }

  @CacheIfFail("billing-fiscal-profiles")
  listAll(): Observable<FiscalProfile[]> {
    return this.list({
      page: 1,
      pageSize: 10000
    }).pipe(map((response) => response.docs));
  }

  @CacheIfFail("billing-fiscal-profiles")
  listAllLight(): Observable<FiscalProfile[]> {
    return this.list({
      page: 1,
      pageSize: 10000,
      filterValues: { light: "true" }
    }).pipe(map((response) => response.docs));
  }

  @UserNotify({
    successMessage: marker("Profilo fiscale creato"),
    errorMessage: marker("Impossibile creare il profilo fiscale")
  })
  create(request: FiscalProfileCreate): Observable<FiscalProfile> {
    return this.http.post<FiscalProfile>(
      `${environment.snoots.api.billing}/fiscal-profiles`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Profilo fiscale aggiornato"),
    errorMessage: marker("Impossibile aggiornare il profilo fiscale")
  })
  update(id: string, request: Partial<FiscalProfileUpdate>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/fiscal-profiles/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Profilo fiscale rimosso"),
    errorMessage: marker("Impossibile rimuovere il profilo fiscale")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/fiscal-profiles/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Sezionale aggiunto"),
    errorMessage: marker("Impossibile aggiungere il sezionale")
  })
  addSectional(
    fiscalProfileId: string,
    payload: SectionalCreate
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.billing}/fiscal-profiles/${fiscalProfileId}/sectionals`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Sezionale aggiornato"),
    errorMessage: marker("Impossibile aggiornare il sezionale")
  })
  updateSectional(
    fiscalProfileId: string,
    sectionalId: string,
    request: SectionalCreate
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/fiscal-profiles/${fiscalProfileId}/sectionals/${sectionalId}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Sezionale rimosso"),
    errorMessage: marker("Impossibile rimuovere il sezionale")
  })
  removeSectional(
    fiscalProfileId: string,
    sectionalId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/fiscal-profiles/${fiscalProfileId}/sectionals/${sectionalId}`
    );
  }
}
